import { Outlet, createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { z } from 'zod';

import { RequireEntitlements } from '@/components/Auth/RequireEntitlements';
import { SettingsPageLayout } from '@/components/Settings/SettingsPageLayout';
import { Invitations } from '@/components/User/Invitations';
import { UserManagement } from '@/components/User/Management/UserManagement';
import { userRoleFilterValues } from '@/components/User/userRoleItems';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';
import { requiredSortingSearchParam } from '@/utils/tableSorting';

const usersSearchSchema = z.object({
  uPageSize: fallback(z.number(), 30).default(30),
  uPageIndex: fallback(z.number(), 0).default(0),
  uSearch: searchParam().optional(),
  uRole: z.enum(userRoleFilterValues).optional(),
  uSorting: requiredSortingSearchParam([{ id: 'name', desc: false }]),
});

export const Route = createFileRoute('/$organizationName/settings/_access/users')({
  component: SecuredUsersPage,
  validateSearch: zodValidator(usersSearchSchema),
});

function SecuredUsersPage() {
  return (
    <RequireEntitlements admins={['isSuperAdmin']}>
      <UsersPage />
    </RequireEntitlements>
  );
}

function UsersPage() {
  const { organizationDisplayName } = useUser();

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Users`;
  }, [organizationDisplayName]);

  return (
    <SettingsPageLayout fullWidthWithPadding hideSeparator innerClassName="space-y-10">
      <Invitations />
      <UserManagement />
      <Outlet />
    </SettingsPageLayout>
  );
}
